import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronRight, ChevronLeft, Check, Zap, Brain, Volume2 } from 'lucide-react';

const STEPS = [
  { id: 1, label: 'Brain', icon: Brain },
  { id: 2, label: 'Voice', icon: Volume2 },
  { id: 3, label: 'Launch', icon: Zap },
];

const BRAIN_OPTIONS = [
  { id: 'qwen', name: 'Qwen2.5', type: 'local', provider: 'Ollama', description: '7B parameters, multilingual' },
  { id: 'llama', name: 'Llama 3', type: 'local', provider: 'Ollama', description: '8B parameters, general purpose' },
  { id: 'bitnet', name: 'BitNet b1.58', type: 'local', provider: 'Local', description: '1-bit quantized, ultra-fast' },
  { id: 'gemini', name: 'Gemini 2.0 Flash', type: 'external', provider: 'Google', description: 'Low-latency cloud AI' },
  { id: 'claude', name: 'Claude 3.5 Sonnet', type: 'external', provider: 'Anthropic', description: 'Reasoning & safety' },
];

const VOICE_OPTIONS = [
  { id: 'piper', name: 'Piper TTS', type: 'utility', provider: 'Local', description: 'Fast, private, on-device' },
  { id: 'utility', name: 'Utility STT', type: 'utility', provider: 'Whisper.cpp', description: 'Speech-to-text, local' },
  { id: 'xtts', name: 'XTTS v2', type: 'clone', provider: 'Coqui', description: 'Voice cloning, expressive' },
  { id: 'clone', name: 'Voice Clone', type: 'clone', provider: 'Custom', description: 'High-fidelity cloning' },
];

function VoiceWizard({ onStartSession }) {
  const [step, setStep] = useState(1);
  const [brain, setBrain] = useState(null);
  const [voice, setVoice] = useState(null);

  const canNext = step === 1 ? brain : step === 2 ? voice : true;

  const handleNext = () => {
    if (step < 3) setStep((s) => s + 1);
  };

  const handlePrev = () => {
    if (step > 1) setStep((s) => s - 1);
  };

  const handleStart = () => {
    onStartSession({ brain, voice });
  };

  return (
    <div className="p-8 max-w-4xl mx-auto">
      {/* Header */}
      <div className="mb-10">
        <h2 className="text-3xl font-bold text-white">Voice Wizard</h2>
        <p className="text-slate-400 mt-1">Configure your sovereign voice pipeline</p>
      </div>

      {/* Step Indicators */}
      <div className="flex items-center justify-center gap-0 mb-12">
        {STEPS.map((s, idx) => (
          <React.Fragment key={s.id}>
            <div className="flex flex-col items-center">
              <div
                className={`step-indicator ${
                  step === s.id
                    ? 'step-indicator-active'
                    : step > s.id
                    ? 'step-indicator-done'
                    : 'step-indicator-pending'
                }`}
              >
                {step > s.id ? <Check className="w-5 h-5" /> : <s.icon className="w-5 h-5" />}
              </div>
              <span
                className={`text-xs mt-2 font-medium ${
                  step === s.id ? 'text-purple-400' : step > s.id ? 'text-emerald-500' : 'text-slate-600'
                }`}
              >
                {s.label}
              </span>
            </div>
            {idx < STEPS.length - 1 && (
              <div
                className={`w-24 h-0.5 mx-3 rounded transition-colors duration-300 ${
                  step > idx + 1 ? 'bg-emerald-600' : 'bg-slate-800'
                }`}
              />
            )}
          </React.Fragment>
        ))}
      </div>

      {/* Step Content */}
      <AnimatePresence mode="wait">
        <motion.div
          key={step}
          initial={{ opacity: 0, x: 30 }}
          animate={{ opacity: 1, x: 0 }}
          exit={{ opacity: 0, x: -30 }}
          transition={{ duration: 0.25, ease: 'easeInOut' }}
        >
          {step === 1 && (
            <div>
              <h3 className="text-xl font-semibold text-white mb-6">Choose your Brain</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {BRAIN_OPTIONS.map((b) => (
                  <button
                    key={b.id}
                    onClick={() => setBrain(b)}
                    className={`text-left p-5 rounded-2xl border transition-all duration-200 ${
                      brain?.id === b.id
                        ? 'border-purple-500 bg-purple-600/10 purple-glow'
                        : 'border-slate-800 bg-slate-900/40 hover:border-slate-700'
                    }`}
                  >
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-white font-semibold">{b.name}</span>
                      <span
                        className={`text-xs font-medium px-2 py-0.5 rounded-full ${
                          b.type === 'local'
                            ? 'bg-emerald-600/20 text-emerald-400'
                            : 'bg-blue-600/20 text-blue-400'
                        }`}
                      >
                        {b.type === 'local' ? 'LOCAL' : 'EXTERNAL'}
                      </span>
                    </div>
                    <p className="text-sm text-slate-400">{b.description}</p>
                    <p className="text-xs text-slate-500 mt-1">via {b.provider}</p>
                  </button>
                ))}
              </div>
            </div>
          )}

          {step === 2 && (
            <div>
              <h3 className="text-xl font-semibold text-white mb-6">Choose Voice Engine</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {VOICE_OPTIONS.map((v) => (
                  <button
                    key={v.id}
                    onClick={() => setVoice(v)}
                    className={`text-left p-5 rounded-2xl border transition-all duration-200 ${
                      voice?.id === v.id
                        ? 'border-amber-500 bg-amber-600/10 amber-glow'
                        : 'border-slate-800 bg-slate-900/40 hover:border-slate-700'
                    }`}
                  >
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-white font-semibold">{v.name}</span>
                      <span
                        className={`text-xs font-medium px-2 py-0.5 rounded-full ${
                          v.type === 'utility'
                            ? 'bg-emerald-600/20 text-emerald-400'
                            : 'bg-purple-600/20 text-purple-400'
                        }`}
                      >
                        {v.type === 'utility' ? 'UTILITY' : 'CLONE'}
                      </span>
                    </div>
                    <p className="text-sm text-slate-400">{v.description}</p>
                    <p className="text-xs text-slate-500 mt-1">via {v.provider}</p>
                  </button>
                ))}
              </div>
            </div>
          )}

          {step === 3 && (
            <div className="glass-panel p-8 max-w-xl mx-auto">
              <div className="text-center mb-8">
                <Zap className="w-12 h-12 text-amber-400 mx-auto mb-3" />
                <h3 className="text-2xl font-bold text-white">Ready to Launch</h3>
                <p className="text-slate-400 mt-1">Review your configuration</p>
              </div>

              <div className="space-y-4 mb-8">
                <div className="flex items-center justify-between p-4 rounded-xl bg-slate-800/50">
                  <div>
                    <p className="text-xs text-slate-500 uppercase tracking-wider">Brain</p>
                    <p className="text-white font-semibold mt-0.5">{brain?.name}</p>
                    <p className="text-xs text-slate-500">via {brain?.provider} · {brain?.type === 'local' ? 'Local' : 'External'}</p>
                  </div>
                  <Brain className="w-6 h-6 text-purple-400" />
                </div>

                <div className="flex items-center justify-between p-4 rounded-xl bg-slate-800/50">
                  <div>
                    <p className="text-xs text-slate-500 uppercase tracking-wider">Voice</p>
                    <p className="text-white font-semibold mt-0.5">{voice?.name}</p>
                    <p className="text-xs text-slate-500">via {voice?.provider} · {voice?.type === 'utility' ? 'Utility' : 'Clone'}</p>
                  </div>
                  <Volume2 className="w-6 h-6 text-amber-400" />
                </div>
              </div>

              <button
                onClick={handleStart}
                className="w-full py-4 bg-purple-600 hover:bg-purple-500 text-white rounded-xl font-bold text-lg transition-all duration-200 purple-glow flex items-center justify-center gap-2"
              >
                <Zap className="w-5 h-5" />
                Start Session
              </button>
            </div>
          )}
        </motion.div>
      </AnimatePresence>

      {/* Navigation Buttons */}
      <div className="flex items-center justify-between mt-10">
        <button
          onClick={handlePrev}
          disabled={step === 1}
          className="flex items-center gap-2 px-5 py-3 rounded-xl text-sm font-medium text-slate-400 hover:text-white hover:bg-slate-800/50 transition-all duration-200 disabled:opacity-30 disabled:cursor-not-allowed"
        >
          <ChevronLeft className="w-4 h-4" />
          Previous
        </button>

        {step < 3 && (
          <button
            onClick={handleNext}
            disabled={!canNext}
            className="flex items-center gap-2 px-6 py-3 rounded-xl text-sm font-medium bg-purple-600 hover:bg-purple-500 text-white transition-all duration-200 disabled:opacity-30 disabled:cursor-not-allowed purple-glow"
          >
            Next
            <ChevronRight className="w-4 h-4" />
          </button>
        )}
      </div>
    </div>
  );
}

export default VoiceWizard;
